import { useCallback, useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import Router from 'next/router';
import { DialogTrigger } from '@radix-ui/react-dialog';
import { IRegion } from '@/types/explorer';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { Progress } from '@/components/shadcn/progress';
import {
  formatNumber,
  formatProgressData,
  getMultiplierFactor,
  getRoundPercent,
  getTotalKm,
} from '@/utils/explorer';
import AreaChart from '@/components/ui/charts/AreaChart';
import CustomTooltip from '@/components/ui/charts/CustomTooltip';
import { Button } from '@/components/shadcn/button';
import { Dialog, DialogContent } from '@/components/shadcn/dialog';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/shadcn/carousel';
import { MapContextProvider } from '@/hooks/useMap';
import { cn } from '@/utils/styles';
import RegionMapStatic from './RegionMapStatic';

function RegionDetails({
  region,
  regions,
}: {
  region: IRegion;
  regions: IRegion[];
}) {
  const progress = getRoundPercent(region.overallProgress || 0);
  const totalKm = formatNumber(getTotalKm(region.totalCoveredL12Cells || 0));
  const uniqueKm = formatNumber(getTotalKm(region.uniqueCoveredL12Cells || 0));
  const contributors = formatNumber(region.totalUsers || 0);
  const multiplier = getMultiplierFactor(
    getRoundPercent(region.multiplierFactor || 0),
  );
  const weight = region.multiplier;
  const activityProgressData = formatProgressData(
    region.activityProgressByWeek || {},
    region.mostRecentDay || '',
  );
  const [screenWidth, setScreenWidth] = useState<number | undefined>();
  useEffect(() => {
    if (window) {
      setScreenWidth(window.innerWidth);
    }
  }, []);
  const coverageProgressData = formatProgressData(
    region.coverageProgressByWeek || {},
    region.mostRecentDay || '',
  );

  const rawData = region.resilienceProgressByWeek || {};
  const transformedRawData = Object.keys(rawData).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: rawData[curr].progress,
    }),
    {},
  );

  const resilienceProgressData = formatProgressData(
    transformedRawData,
    region.mostRecentDay || '',
  );

  const {
    roadLengthByTypeKm,
    airportData,
    bridgeData,
    images,
    description,
    updatedAt,
  } = region.metadata;
  const totalRoadKm = (
    roadLengthByTypeKm.total ??
    Object.values(roadLengthByTypeKm).reduce((res, val) => res + val, 0)
  ).toLocaleString('en-US');

  const quickFactsTitles = [
    'Roads',
    airportData.length > 0 ? 'Airports' : null,
    bridgeData.length > 0 ? 'Bridges' : null,
  ].filter(Boolean) as Array<'Roads' | 'Airports' | 'Bridges'>;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { motorwayTrunks, primarySecondary, tertiaryResidential, minorAlleys } =
    roadLengthByTypeKm;
  const quickFactsTableData: Record<
    'Roads' | 'Airports' | 'Bridges',
    { title: string; value?: string }[]
  > = {
    Roads: [
      { title: 'Total Roads', value: `${totalRoadKm} Km` },
      {
        title: 'Motorways & Trunks',
        value: `${motorwayTrunks.toLocaleString('en-US')} Km`,
      },
      {
        title: 'Primary & Secondary',
        value: `${primarySecondary.toLocaleString('en-US')} Km`,
      },
      {
        title: 'Tertiary & Residential',
        value: `${tertiaryResidential.toLocaleString('en-US')} Km`,
      },
      {
        title: 'Minor & Alleys',
        value: `${minorAlleys.toLocaleString('en-US')} Km`,
      },
    ],
    Airports: airportData.map(entry => ({
      title: entry.name,
    })),
    Bridges: bridgeData.map(entry => ({
      title: entry.name,
    })),
  };

  // Only render 5 rows if quickFacts section is collapsed.
  const numRowsData = Math.max(
    ...Object.values(quickFactsTableData).map(entries => entries.length),
  );
  const numRowsToRender = isCollapsed ? Math.min(5, numRowsData) : numRowsData;

  const onUseScoutClick = useCallback(() => {
    Router.push(`/scout`, undefined, {
      shallow: true,
    });
  }, []);
  const onMapPreviewClick = useCallback(() => {
    Router.push(`/network/regions/${region.name}/map`, undefined, {
      shallow: true,
    });
  }, [region.name]);

  const [imageIdx, setImageIdx] = useState(0);

  return (
    <>
      <div className="flex flex-col gap-2 md:gap-4">
        {/* Imagery Section */}
        {images?.length > 0 && (
          <>
            <Dialog>
              {/* Fullscreen view */}
              <Card className="hidden w-full border-none bg-neutral-900 md:block">
                <CardHeader className="pb-0">
                  <CardTitle className="text-xl">
                    Updated Street-Views of {region.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid w-full grid-cols-4 grid-rows-2 gap-4 p-6">
                  {images.map(({ url, altText }, i) => (
                    <DialogTrigger
                      key={i}
                      className={i === 0 ? 'col-span-2 row-span-2' : ''}>
                      <img
                        className="aspect-[16/9] size-full rounded object-cover"
                        alt={altText}
                        src={url}
                        onClick={() => {
                          setImageIdx(i);
                        }}
                      />
                    </DialogTrigger>
                  ))}

                  <div className="flex aspect-[16/9] flex-col items-center justify-center gap-4 overflow-hidden rounded border border-neutral-700 bg-neutral-800">
                    <div className="hidden max-w-[200px] text-center text-sm md:text-xl lg:block">
                      Explore imagery from around the globe
                    </div>
                    <Button onClick={() => onUseScoutClick()}>Use Scout</Button>
                  </div>
                </CardContent>
              </Card>
              {/* Modal content */}
              <DialogContent className=" w-screen max-w-[90vw]">
                <Carousel className="size-full" opts={{ startIndex: imageIdx }}>
                  <CarouselContent>
                    {images.map(({ url, altText }, i) => (
                      <CarouselItem key={i}>
                        <img
                          className="aspect-[16/9] size-full"
                          alt={altText}
                          src={url}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              </DialogContent>
            </Dialog>
            {/* Mobile view */}
            <Card className="w-full rounded border-none bg-neutral-900 md:hidden">
              <CardHeader className="p-2 pb-0">
                <CardTitle className="text-[8px] leading-normal">
                  Updated Street-Views of {region.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <Carousel className="rounded">
                  <CarouselContent>
                    {images.map(({ url, altText }, i) => (
                      <CarouselItem key={i}>
                        <img
                          className="aspect-[16/9] rounded object-cover"
                          alt={altText}
                          src={url}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-4" />
                  <CarouselNext className="right-4" />
                </Carousel>
              </CardContent>
            </Card>
          </>
        )}

        {/* Bee Maps Data Section  */}
        <div className="flex w-full flex-row gap-4">
          {[
            { title: 'Total Road Km', value: totalRoadKm },
            { title: 'Total Road Km Mapped', value: totalKm },
            { title: 'Unique Road Km Mapped', value: uniqueKm },
          ].map(({ title, value }, i) => (
            <Card
              key={i}
              className="flex flex-1 flex-col justify-start rounded border-none bg-neutral-900">
              <CardHeader className="p-2 pb-0 text-2xs md:p-6 md:pb-2 md:text-xl">
                <CardTitle className="leading-tight">{title}</CardTitle>
              </CardHeader>
              <CardContent className="p-2 pt-0 text-xs text-azure-400 md:p-6 md:pt-0 md:text-2xl">
                <p className="leading-normal">{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Facts Section */}
        <Card className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg ">
          <CardHeader className="p-2 pb-0 text-2xs md:p-6 md:pb-1 md:text-xl">
            <CardTitle>
              <div>Quick Facts for {region.name}</div>
            </CardTitle>
            <CardDescription className="mt-4 text-2xs text-neutral-500 md:text-xl">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex p-2 py-0 text-sm md:p-4 md:py-0">
            {/* Layout for desktop view */}
            <table className="hidden min-w-full table-fixed md:table">
              <thead>
                <tr>
                  {quickFactsTitles.map(title => (
                    <th
                      key={title}
                      className="w-1/3 p-3 pb-0 text-left text-base font-normal">
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(numRowsData)].map((_, row) => (
                  <tr
                    key={row}
                    className={row + 1 > numRowsToRender ? 'hidden' : ''}>
                    {quickFactsTitles.map(title => {
                      const cellData = quickFactsTableData[title][row];

                      if (!cellData)
                        return <td key={title} className="p-3"></td>;

                      return (
                        <td
                          key={title}
                          className="p-3 pb-0 align-baseline text-lg">
                          <span>{cellData.title}</span>
                          {cellData.value && (
                            <span className="ml-4 text-neutral-500">
                              {cellData.value}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Layout for mobile view */}
            <div className="w-full text-2xs md:hidden">
              {quickFactsTitles
                .slice(0, isCollapsed ? 1 : quickFactsTitles.length)
                .map((title, i) => {
                  const entries = quickFactsTableData[title];
                  return (
                    <div key={i}>
                      <div className="mt-2 text-left font-bold">{title}</div>
                      <div
                        className={cn(
                          'mt-2 grid gap-2',
                          // 3-column layout for roads, 1-column layout for bridges and airports
                          { 'grid-cols-3': i === 0, 'grid-cols-1': i !== 0 },
                        )}>
                        {entries.map(
                          ({ title: entryTitle, value }, entryIdx) => (
                            <div key={entryIdx}>
                              <div className="text-neutral-300">
                                {entryTitle}
                              </div>
                              <div className="text-neutral-500">{value}</div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>

          <CardFooter className="p-2 text-sm md:p-6">
            <div className="flex flex-1 justify-between border-t border-neutral-600 pt-1 md:pt-2">
              {numRowsData > 5 && (
                <div
                  className="flex cursor-pointer items-center gap-1 text-3xs md:text-base"
                  onClick={() => setIsCollapsed(!isCollapsed)}>
                  {isCollapsed
                    ? 'Expand Region Facts'
                    : 'Collapse Region Facts'}
                  {isCollapsed ? (
                    <ChevronDownIcon width={8} />
                  ) : (
                    <ChevronUpIcon width={8} />
                  )}
                </div>
              )}
              <div className="flex items-center justify-center text-3xs text-muted-foreground md:text-base">
                Last Updated: {new Date(updatedAt).toLocaleDateString()}
              </div>
            </div>
          </CardFooter>
        </Card>

        {/* Map Preview Section */}
        <div
          className="aspect-[16/9] max-h-[400px] w-full cursor-pointer overflow-hidden rounded bg-neutral-900 p-3 md:p-6"
          onClick={() => onMapPreviewClick()}>
          <MapContextProvider
            key="region-map-static"
            mapType="coverage"
            regionName={region.name}
            regions={regions}
            screenWidth={screenWidth}
            options={{
              enableStyles: false,
              enableLocation: false,
              enableSearch: false,
              rememberLocation: false,
              enableInteraction: false,
            }}>
            <RegionMapStatic region={region} regions={regions} />
          </MapContextProvider>
        </div>

        {/* Number & Charts Section */}
        <div className="text-2xs font-bold md:text-xl">
          Insights on the Hivemapper Network in {region.name}
        </div>
        <div className="flex w-full flex-col gap-2 md:flex-row md:gap-4">
          {/* Numbers */}
          <div className="grid w-full grid-cols-2 gap-2 md:w-1/3 md:grid-cols-1 md:gap-4">
            {[
              { title: 'Progress', value: progress },
              { title: 'Contributors', value: contributors },
              { title: 'Multiplier', value: multiplier },
              { title: 'Weight', value: weight },
            ].map(({ title, value }, i) => (
              <Card
                key={i}
                className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg ">
                <CardHeader className="p-2 pb-0 text-2xs md:p-6 md:pb-1 md:text-xl">
                  <CardTitle>{title}</CardTitle>
                </CardHeader>
                <CardContent className="p-2 pt-0 text-sm text-azure-400 md:p-6 md:pt-0 md:text-2xl">
                  <p className="mb-2 mt-1 leading-tight">
                    {value} {title === 'Progress' && '%'}
                  </p>
                  {title === 'Progress' && (
                    <Progress
                      className="h-1.5 bg-neutral-600 [&>*]:bg-azure-400"
                      value={value as number}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts */}
          <div className="flex w-full flex-col gap-2 md:w-2/3 md:gap-4">
            {[
              {
                title: 'Activity Progress',
                data: activityProgressData,
                value: region.activityProgress,
              },
              {
                title: 'Coverage Progress',
                data: coverageProgressData,
                value: region.coverageProgress,
              },
              {
                title: 'Resilience Progress',
                data: resilienceProgressData,
                value: region.resilienceProgress.progress,
              },
            ].map(({ title, data, value }, i) => (
              <Card
                key={i}
                className="w-full rounded border-none bg-neutral-900">
                <CardHeader className="p-2 pb-0 text-2xs md:p-6 md:pb-2 md:text-xl">
                  <CardTitle className="leading-tight tracking-normal">
                    {title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 py-0 md:px-6">
                  <p className="mb-1 text-xs text-azure-400 md:text-2xl">
                    {getRoundPercent(value || 0)}%
                  </p>
                  <div className="h-32 w-full overflow-hidden md:h-36">
                    <AreaChart
                      data={data}
                      customTooltip={(props: any) => (
                        <CustomTooltip
                          line1={`${Math.round(
                            props?.payload[0]?.value || 0,
                          ).toLocaleString('en-US')}% ${title}`}
                          {...props}
                        />
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default RegionDetails;
